<template>
  <div class="auth-form">
    <h2 class="auth-title">创建账户</h2>
    <p v-if="!registerEnabled" class="login-notice">系统维护中，暂不开放注册</p>
    <p v-if="registerSuccess" class="register-success">{{ registerSuccess }}</p>
    <p v-if="registerError" class="login-error">{{ registerError }}</p>
    <!--
      ⚠️ 每个输入框都必须写明 name + autocomplete，不能省。
      表单结构是「一堆 div + 按钮」，没有 <form>，早期所有输入框都没有 name/autocomplete，
      浏览器的自动填充只能靠「位置启发式」猜字段语义 —— 而邀请码输入框恰好紧挨着密码框，
      于是 Chrome 把它判定成了「登录用户名框」，页面一打开就把已保存的邮箱
      （如 me@mole9630.cn）填了进去。用户看到的是一串邮箱、以为是邮箱框，
      根本找不到填邀请码的地方；更糟的是该字段带 @blur="checkCode"，
      一失焦就把这串邮箱当邀请码发给后端，直接弹「邀请码无效」。
      修法：① 邀请码框 autocomplete="off"；② 两个密码框声明 new-password，
      让浏览器不再把整块识别成登录表单；③ 其余字段给出正确的语义提示。
    -->
    <div class="input-group icon-input">
      <i class="fas fa-user"></i>
      <input type="text" name="nickname" autocomplete="nickname" placeholder="用户名" v-model="registerNickname" />
    </div>
    <div class="input-group icon-input">
      <i class="fas fa-envelope"></i>
      <input type="email" name="email" autocomplete="email" placeholder="邮箱" v-model="registerEmail" />
    </div>
    <div class="input-group code-group icon-input">
      <i class="fas fa-key"></i>
      <input type="text" name="verifyCode" autocomplete="one-time-code" placeholder="邮箱验证码" v-model="registerVerifyCode" />
      <button type="button" class="code-btn" :disabled="codeSending || codeCountdown > 0" @click="sendCode">
        {{ codeCountdown > 0 ? `${codeCountdown} 秒后重试` : '获取验证码' }}
      </button>
    </div>
    <div v-if="inviteCodeRequired" class="input-group icon-input">
      <i class="fas fa-ticket-alt"></i>
      <input
        type="text"
        name="inviteCode"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        placeholder="邀请码（必填）"
        v-model="registerInviteCode"
        @blur="checkCode"
      />
    </div>
    <div class="input-group icon-input">
      <i class="fas fa-lock"></i>
      <input type="password" name="password" autocomplete="new-password" placeholder="密码" v-model="registerPassword" />
    </div>
    <div class="input-group icon-input">
      <i class="fas fa-check-circle"></i>
      <input type="password" name="confirmPassword" autocomplete="new-password" placeholder="确认密码" v-model="registerConfirmPassword" />
    </div>
    <!--
      同意条款：PIPL 要求「收集个人信息应取得同意」，所以默认不勾选、不勾选不能提交。
      ⚠️ 不要改成默认勾选 —— 默认勾选属于「以默示方式取得同意」，监管口径不认。
    -->
    <label class="agree-row" :class="{ 'is-error': agreeError }">
      <input type="checkbox" name="agreeTerms" v-model="registerAgree" />
      <span>
        我已阅读并同意
        <a href="/terms" target="_blank" rel="noopener" @click.stop>《用户协议》</a>
        和
        <a href="/privacy" target="_blank" rel="noopener" @click.stop>《隐私政策》</a>
      </span>
    </label>
    <button class="auth-submit" @click.prevent="doRegister" :disabled="registerLoading || !registerEnabled">
      {{ !registerEnabled ? '暂不开放注册' : (registerLoading ? '注册中...' : '注册') }}
    </button>
    <p class="social-text">或使用社交账号注册</p>
    <div class="social-icons">
      <span class="social-icon">G</span>
      <span class="social-icon">f</span>
      <span class="social-icon">Git</span>
      <span class="social-icon">in</span>
    </div>
    <p class="switch-text">
      已有账号？
      <a href="#" @click.prevent="$emit('show-login')">立即登录</a>
    </p>
  </div>
</template>

<script>
import { sendEmailCode, register as registerApi } from '@/api/user.js'
import { checkInviteCode } from '@/api/system.js'

export default {
  name: 'RegisterForm',
  props: {
    // 注册开关（来自后端 system_settings）。
    // 与 LoginForm 的 loginEnabled 对称：关闭时禁用提交并给出维护提示。
    // 入口按钮虽然已经隐藏，但用户可能停留在本页期间开关被关闭，所以这里仍要拦一道。
    registerEnabled: {
      type: Boolean,
      default: true
    },
    // 注册是否需要邀请码（来自后端 system_settings）：开启时才渲染邀请码输入框
    inviteCodeRequired: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      registerEmail: '',
      registerPassword: '',
      registerConfirmPassword: '',
      registerVerifyCode: '',
      registerNickname: '',
      registerInviteCode: '',
      registerError: '',
      registerSuccess: '',
      registerLoading: false,
      // 是否已勾选同意《用户协议》《隐私政策》。默认 false：
      // 默认勾选等于「以默示方式取得同意」，PIPL 口径不认
      registerAgree: false,
      // 只在「点过提交但没勾」时才标红，避免一进页面就一片红
      agreeError: false,
      codeSending: false,
      codeCountdown: 0,
      codeTimer: null
    }
  },
  mounted() {
    // 兜底：浏览器自动填充不受 Vue 控制，可能在挂载后才把已保存的邮箱/账号
    // 塞进邀请码输入框（该框紧邻密码框，没有 name/autocomplete 时会被误判成「用户名框」）。
    // 这里挂载后清一次，与模板上的 autocomplete="off" 形成双保险。
    this.registerInviteCode = ''
  },
  methods: {
    /**
     * 邀请码失焦预校验：只校验不核销（不消耗次数），提前把「已禁用 / 已过期 / 已用完」
     * 这类原因告诉用户，避免填完整个表单提交才报错。
     */
    async checkCode() {
      const code = (this.registerInviteCode || '').trim()
      if (!code) {
        return
      }
      // 兜底：邀请码字符集只有 [A-Z0-9]（见 backend/scripts/gen_invite_codes.py），
      // 绝不可能含 '@'。一旦出现 '@'，说明这是浏览器自动填充进来的邮箱，
      // 而不是用户手输的邀请码 —— 直接清掉，别拿它去问后端，
      // 否则用户还没开始填表就会看到一个莫名其妙的「邀请码无效」。
      if (code.includes('@')) {
        this.registerInviteCode = ''
        this.registerError = ''
        return
      }
      try {
        const data = await checkInviteCode(code)
        if (!data.success || data.code !== 200) {
          this.registerError = data.message || '邀请码无效'
        } else {
          this.registerError = ''
          this.registerSuccess = '邀请码可用'
        }
      } catch (e) {
        // 预校验失败不阻塞填写，提交时后端还会再校验一次
      }
    },
    async sendCode() {
      this.registerError = ''
      this.registerSuccess = ''
      // 注册关闭时连验证码都不该发出去 —— 否则用户拿到码却注册不了，只会更困惑。
      // 与后端 sendEmailCode 的 REGISTER 分支 assertRegisterEnabled() 保持一致。
      if (!this.registerEnabled) {
        this.registerError = '系统维护中，暂不开放注册'
        return
      }
      if (!this.registerEmail) {
        this.registerError = '请输入邮箱'
        return
      }
      this.codeSending = true
      try {
        const data = await sendEmailCode(this.registerEmail, 'REGISTER')
        if (!data.success || data.code !== 200) {
          this.registerError = data.message || '验证码发送失败'
          return
        }
        this.registerSuccess = '验证码已发送，请查收邮件'
        this.startCodeCountdown(60)
      } catch (e) {
        this.registerError = '网络异常，请稍后重试'
      } finally {
        this.codeSending = false
      }
    },
    startCodeCountdown(seconds) {
      this.codeCountdown = seconds
      if (this.codeTimer) {
        clearInterval(this.codeTimer)
      }
      this.codeTimer = setInterval(() => {
        this.codeCountdown--
        if (this.codeCountdown <= 0) {
          clearInterval(this.codeTimer)
          this.codeTimer = null
        }
      }, 1000)
    },
    async doRegister() {
      this.registerError = ''
      this.registerSuccess = ''
      // 前端拦截只是体验优化，真正的裁决在后端 register() 入口（防止有人直接打接口）
      if (!this.registerEnabled) {
        this.registerError = '系统维护中，暂不开放注册'
        return
      }
      // 同意条款必须在最前面拦：不勾选就不该往下走，
      // 否则用户填完整张表才被告知「请先同意」，体验很差
      if (!this.registerAgree) {
        this.agreeError = true
        this.registerError = '请先阅读并同意《用户协议》和《隐私政策》'
        return
      }
      this.agreeError = false
      if (!this.registerEmail || !this.registerPassword || !this.registerConfirmPassword || !this.registerVerifyCode) {
        this.registerError = '请填写完整注册信息'
        return
      }
      if (this.inviteCodeRequired && !(this.registerInviteCode || '').trim()) {
        this.registerError = '请填写邀请码'
        return
      }
      if (this.registerPassword !== this.registerConfirmPassword) {
        this.registerError = '两次输入的密码不一致'
        return
      }
      this.registerLoading = true
      try {
        const registeredEmail = this.registerEmail
        const data = await registerApi({
          email: this.registerEmail,
          password: this.registerPassword,
          confirmPassword: this.registerConfirmPassword,
          verifyCode: this.registerVerifyCode,
          nickname: this.registerNickname,
          // 开关关闭时不传，避免把无意义的空串塞给后端
          inviteCode: this.inviteCodeRequired ? (this.registerInviteCode || '').trim() : undefined,
          registerSource: 'web'
        })
        if (!data.success || data.code !== 200) {
          this.registerError = data.message || '注册失败'
          return
        }
        this.registerSuccess = '注册成功，请登录'
        this.clearRegisterForm()
        this.$emit('register-success', registeredEmail)
        setTimeout(() => {
          this.$emit('show-login')
        }, 1200)
      } catch (e) {
        this.registerError = '网络异常，请稍后重试'
      } finally {
        this.registerLoading = false
      }
    },
    clearRegisterForm() {
      this.registerEmail = ''
      this.registerPassword = ''
      this.registerConfirmPassword = ''
      this.registerVerifyCode = ''
      this.registerNickname = ''
      this.registerInviteCode = ''
      if (this.codeTimer) {
        clearInterval(this.codeTimer)
        this.codeTimer = null
      }
      this.codeCountdown = 0
    }
  },
  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer)
    }
  }
}
</script>

<style scoped>
.auth-form{
  position:relative;
  height:100%;
  margin:0 auto;
  width:100%;
  max-width:384px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
}

.auth-title{
  font-size:2rem;
  font-weight:800;
  color:#333;
  margin-bottom:1.75rem;
}

.input-group{
  width:100%;
  margin-bottom:0.875rem;
}

.input-group input{
  width:100%;
  padding:0.875rem 1rem;
  border:none;
  border-radius:0.625rem;
  background:#f0f0f0;
  font-size:0.875rem;
  color:#333;
  font-family:inherit;
  outline:none;
}

.input-group input::placeholder{
  color:#999;
}

.login-error{
  width:100%;
  margin-bottom:0.75rem;
  font-size:0.75rem;
  color:#e74c3c;
  text-align:left;
}

/* 维护提示条：与 LoginForm 的 .login-notice 保持一致 */
.login-notice{
  width:100%;
  margin-bottom:1rem;
  padding:0.625rem 0.75rem;
  border-radius:0.5rem;
  background:#fff6e5;
  border:1px solid #f0d9a8;
  font-size:0.75rem;
  color:#8a6d1f;
  text-align:left;
}

.register-success{
  width:100%;
  margin-bottom:0.75rem;
  font-size:0.75rem;
  color:#558b2f;
  text-align:left;
}

.icon-input{
  position:relative;
}

.icon-input i{
  position:absolute;
  left:1rem;
  top:50%;
  transform:translateY(-50%);
  color:#999;
  font-size:0.875rem;
  z-index:1;
}

.icon-input input{
  padding-left:2.5rem;
}

.code-group{
  display:flex;
  gap:0.625rem;
}

.code-group input{
  flex:1;
}

.code-btn{
  padding:0 1rem;
  border:none;
  border-radius:0.625rem;
  background:#558b2f;
  color:#fff;
  font-size:0.75rem;
  font-weight:700;
  cursor:pointer;
  font-family:inherit;
  white-space:nowrap;
  transition:background 0.2s ease;
}

.code-btn:hover{
  background:#4a7c28;
}

.code-btn:disabled{
  background:#9abf7a;
  cursor:not-allowed;
}

.auth-submit{
  width:100%;
  padding:0.875rem;
  border:none;
  border-radius:0.625rem;
  background:#558b2f;
  color:#fff;
  font-size:0.9375rem;
  font-weight:700;
  cursor:pointer;
  font-family:inherit;
  margin-bottom:1.125rem;
  transition:background 0.2s ease;
}

.auth-submit:hover{
  background:#4a7c28;
}

.auth-submit:disabled{
  background:#9abf7a;
  cursor:not-allowed;
}

.social-text{
  font-size:0.75rem;
  color:#888;
  margin-bottom:0.875rem;
}

.social-icons{
  display:flex;
  gap:0.75rem;
  margin-bottom:1.125rem;
}

.social-icon{
  width:40px;
  height:40px;
  border:1px solid #ddd;
  border-radius:0.5rem;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:0.8125rem;
  font-weight:700;
  color:#555;
  cursor:pointer;
  transition:all 0.2s ease;
}

.social-icon:hover{
  border-color:#558b2f;
  color:#558b2f;
}

.switch-text{
  font-size:0.75rem;
  color:#888;
}

.switch-text a{
  color:#558b2f;
  font-weight:700;
  text-decoration:none;
}

/* 同意条款：与输入框同宽、行内对齐；勾选框用浏览器原生样式，保持轻量 */
.agree-row{
  display:flex;
  align-items:flex-start;
  gap:0.5rem;
  margin:0.25rem 0 0.875rem;
  font-size:0.75rem;
  line-height:1.5;
  color:#888;
  cursor:pointer;
  text-align:left;
}

.agree-row input[type="checkbox"]{
  margin-top:0.15rem;
  flex:0 0 auto;
  accent-color:#558b2f;
  cursor:pointer;
}

.agree-row a{
  color:#558b2f;
  font-weight:700;
  text-decoration:none;
}

/* 未勾选就提交时才标红，平时保持中性灰，避免一进页面就一片红 */
.agree-row.is-error{
  color:#d93025;
}

.agree-row.is-error a{
  color:#d93025;
}
</style>
